import { Box, Chip, Stack, Typography } from '@mui/material'
import React, { useMemo } from 'react'

import { AgentDetailsDrawerButtons } from 'components/configurations/agents/AgentDetailsDrawerButtons'
import {
  formatApplicationLabels,
  isFailureFlagsApplication
} from 'components/failure-flags/failure-flags-utils'
import Icon from 'components/Icon'
import { muiTheme } from 'mui-theme'
import { type Agent, AgentStatus } from 'types/api/agents/agents'

import type { Client } from 'types/api/attacks/client'
import type { FailureFlagApplication } from 'types/api/failure-flags/failure-flags'

export const AgentDetails: React.FC<{
  agent: Agent
  agentDetails: Client | FailureFlagApplication
}> = ({ agent, agentDetails }) => {
  const isFailureFlagsApp = isFailureFlagsApplication(agentDetails)
  const isDeactivated = agent.status === AgentStatus.DEACTIVATED

  const agentName = isFailureFlagsApp
    ? agentDetails.name
    : agentDetails.identifier

  const tags = useMemo(() => {
    if (isFailureFlagsApp) {
      return Object.entries(agentDetails.labels ?? []).flatMap(
        ([labelKey, applicationLabels]) =>
          `${labelKey}: ${formatApplicationLabels(labelKey, applicationLabels)}`
      )
    }

    return Object.entries(agentDetails.tags ?? []).map(
      ([key, value]) => `${key}: ${value}`
    )
  }, [agentDetails, isFailureFlagsApp])

  const warnings = useMemo(() => {
    if (isFailureFlagsApp) {
      return []
    }

    return agentDetails.warnings
  }, [agentDetails, isFailureFlagsApp])

  return (
    <Stack
      data-testid={`agent-details-drawer-content-${agent.id}`}
      sx={{ rowGap: 4 }}
    >
      <Stack sx={{ rowGap: 2 }}>
        <Typography variant="h6">{agentName}</Typography>
        {warnings.length > 0 && (
          <Stack data-testid="agent-details-drawer-warnings-list">
            {warnings.map((warning, index) => (
              <Typography
                key={`warning.key-${index}`}
                color="warning.main"
                data-testid={`agent-details-drawer-warning-${warning.key}`}
                variant="body1"
              >
                <Icon
                  css={{ marginRight: muiTheme.spacing(1) }}
                  icon="solidWarning"
                />
                {warning.summary}
              </Typography>
            ))}
          </Stack>
        )}
      </Stack>
      {tags.length > 0 ? (
        <Box data-testid="agent-details-drawer-tags-list">
          {tags.map((tag) => (
            <Chip
              key={tag}
              data-testid={`agent-details-drawer-tag-${tag}`}
              label={tag}
              size="small"
              sx={{ width: 'fit-content', margin: 0.5 }}
            />
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Typography>No tags found for this agent</Typography>
        </Box>
      )}
      <AgentDetailsDrawerButtons agent={agent} isDeactivated={isDeactivated} />
    </Stack>
  )
}
